import React, { useContext, useState } from 'react';
import { hasWritePermission } from '../../../components/Authorization/hasWritePermission';
import { useHistory } from 'react-router';
import { deleteData, fetchData } from '../../service/dataServices';
import { getTableMenuItems, pageSizeOptions } from '../../components/common-constants';
import { Button, Col, Divider, Dropdown, Input, Popconfirm, Row, Select, Space, Table, Typography } from 'antd';
import { DeleteOutlined, DownloadOutlined, DownOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import CustomContent from '../../../components/Content/CustomContent';
import moment from 'moment';
import { useExcelExporter } from '../../components/export/useExcelExporter';
import { FilterContext } from '../../../App';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const ShowMovements = () => {
  const hasWritePerm = hasWritePermission('movement');

  const history = useHistory();
  const { movementFilters: filters, setMovementFilters: setFilters } = useContext(FilterContext);

  const [selectedItem, setSelectedItem] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);
  const [dropdowns, setDropdowns] = useState(null);
  const { excelExportModal, onExport } = useExcelExporter('movements', selectedRowKeys);

  const response = fetchData('/api/movements', data => setData(data.content), filters);
  const deleteMutation = deleteData('/api/movements');
  fetchData('/api/dropdowns', setDropdowns);

  const onMenuClick = action => {
    if (action.key === 'edit' && selectedItem.id) {
      history.push('/save-movement/' + selectedItem.id);
    }
  };

  const handleSingleDelete = () => {
    deleteMutation.mutate({ idList: selectedItem.id });
  };

  const handleTableChange = pagination => {
    if (pagination.current - 1 !== filters?.page) {
      setFilters(prevState => ({
        ...prevState,
        page: pagination.current - 1,
      }));
    }
    if (pagination.pageSize !== filters?.size) {
      setFilters(prevState => ({
        ...prevState,
        size: pagination.pageSize,
      }));
    }
  };

  const onSearch = value => {
    if (value !== '') {
      setFilters(prevState => ({
        ...prevState,
        searchText: value,
        page: 0,
      }));
    } else {
      const val = { ...filters };
      delete val.searchText;
      val.page = 0;
      setFilters(val);
    }
  };

  const onDelete = () => {
    deleteMutation.mutate({ idList: selectedRowKeys.join(',') });
    setSelectedRowKeys([]);
  };

  const onAdd = () => {
    history.push('/add-movement');
  };

  const columns = [
    {
      title: 'Inventory Number',
      dataIndex: 'inventoryNumber',
      key: 'inventoryNumber',
      render: (text, record) => (
        <>
          <Button
            type="link"
            onClick={() => history.push('/save-movement/' + record.id)}
          >
            {text}
          </Button>
          <Text copyable={{ text: text }}></Text>
        </>
      ),
    },
    {
      title: 'Object Name',
      dataIndex: 'objectName',
      key: 'objectName',
    },
    {
      title: 'Report ID',
      dataIndex: 'reportId',
      key: 'reportId',
    },
    {
      title: 'Movement Type',
      dataIndex: 'movementType',
      key: 'movementType',
    },
    {
      title: 'Reason For Movement',
      dataIndex: 'reasonForMovement',
      key: 'reasonForMovement',
    },
    {
      title: 'Date of Movement',
      dataIndex: 'dateOfMovement',
      key: 'dateOfMovement',
      render: (text) => {
        return text ? moment(text).format('YYYY/MM/DD') : text;
      },
    },
    {
      title: 'Date of Return',
      dataIndex: 'dateOfReturn',
      key: 'dateOfReturn',
      render: (text) => {
        return text ? moment(text).format('YYYY/MM/DD') : text;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: () => hasWritePerm ? (
        <span>
          <Dropdown overlay={menu} trigger={['click']}>
            <Button type="link">
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        </span>
      ) : '',
    },
  ];

  const menu = getTableMenuItems(onMenuClick, handleSingleDelete);

  return (
    <CustomContent>
      <Row>
        <Col span={16}>
          <Space direction="horizontal" style={{ width: '100%' }} wrap>
            <Search
              defaultValue={filters ? filters.searchText : ''}
              style={{ width: '30vh' }}
              placeholder="Search"
              onSearch={value => onSearch(value)}
              allowClear
            />
            <Select
              defaultValue={filters ? filters.movementType : undefined}
              placeholder="Movement Type"
              allowClear
              onClear={() => setFilters(prev => ({ ...prev, movementType: null, page: 0 }))}
              onSelect={value => setFilters(prev => ({ ...prev, movementType: value, page: 0 }))}
              style={{ width: '20vh' }}
            >
              {
                dropdowns?.movementType?.map(option => (
                  <Option value={option.id} key={option.id}>{option.title}</Option>
                ))
              }
            </Select>
            <Select
              defaultValue={filters ? filters.reasonForMovement : undefined}
              placeholder="Reason For Movement"
              allowClear
              onClear={() => setFilters(prev => ({ ...prev, reasonForMovement: null, page: 0 }))}
              onSelect={value => setFilters(prev => ({ ...prev, reasonForMovement: value, page: 0 }))}
              style={{ width: '20vh' }}
            >
              {
                dropdowns?.reasonForMovement?.map(option => (
                  <Option value={option.id} key={option.id}>{option.title}</Option>
                ))
              }
            </Select>
          </Space>
        </Col>
        <Col span={8}>
          <Row justify="end">
            <Space direction="horizontal">
              <Button
                onClick={onExport}
                icon={<DownloadOutlined />}
              >
                Export
              </Button>
              {hasWritePerm && (
                <>
                  <Popconfirm
                    title="Confirmation"
                    description="Are you sure to delete?"
                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    onConfirm={onDelete}
                    okButtonProps={{ size: 'middle' }}
                    okText="Yes"
                    cancelButtonProps={{ size: 'middle' }}
                    styles={{ body: { padding: 16 } }}
                    disabled={selectedRowKeys.length === 0}
                  >
                    <Button
                      disabled={selectedRowKeys.length === 0}
                      icon={<DeleteOutlined />}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                  <Button onClick={onAdd} icon={<PlusOutlined />} type="primary">
                    Add
                  </Button>
                </>
              )}
            </Space>
          </Row>
        </Col>
      </Row>
      <Divider />
      <Table
        dataSource={data}
        columns={columns}
        loading={response.isFetching}
        rowKey={record => record.id}
        onChange={handleTableChange}
        size='middle'
        pagination={{
          pageSize: filters?.size,
          total: response.data ? response.data.data.totalElements : 0,
          showSizeChanger: true,
          current: filters ? filters.page + 1 : 1,
          pageSizeOptions: pageSizeOptions,
          showTotal: (total, range) => {
            return `${range[0]}-${range[1]} of ${total} items`;
          },
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: selected => setSelectedRowKeys(selected),
        }}
        onRow={record => {
          return {
            onClick: () => setSelectedItem(record),
          };
        }}
      />
      {excelExportModal}
    </CustomContent>
  );
};

export default ShowMovements;

